import { useEffect, useRef, useState } from "react";
import ScoreSubmit from "@/components/landing/games/ScoreSubmit";
import LegoConfetti from "@/components/landing/LegoConfetti";

const BRICKS = ["#D01012", "#F7C500", "#0067BF", "#00923F", "#F57D20"];
const BRICK_DARK = { "#D01012": "#8F0A0C", "#F7C500": "#B38F00", "#0067BF": "#004A8A", "#00923F": "#00602A", "#F57D20": "#B85A0E" };
const TRACK = 280;
const BRICK_W = 120;
const TOLERANCE = 40;
const MAX_HEIGHT = 11;

const Brick = ({ color, style, moving }) => (
  <div
    className="absolute"
    data-testid={moving ? "stack-moving-brick" : undefined}
    style={{ width: `${BRICK_W / TRACK * 100}%`, ...style }}
  >
    <div className="flex justify-center gap-4 -mb-[3px] relative z-10">
      {[0, 1, 2].map((s) => (
        <span key={s} className="w-4 h-1.5 rounded-t-sm" style={{ backgroundColor: color, borderTop: `2px solid ${BRICK_DARK[color]}` }} />
      ))}
    </div>
    <div
      className="h-5 rounded-[3px] shadow-sm"
      style={{ backgroundColor: color, borderBottom: `3px solid ${BRICK_DARK[color]}` }}
    />
  </div>
);

export default function StackTheBricks() {
  const [stack, setStack] = useState([]);
  const [pos, setPos] = useState(0);
  const [running, setRunning] = useState(false);
  const [over, setOver] = useState(false);
  const [burst, setBurst] = useState(false);
  const dirRef = useRef(1);
  const posRef = useRef(0);
  const rafRef = useRef(null);

  const won = stack.length >= MAX_HEIGHT;

  useEffect(() => {
    if (won) setBurst(true);
  }, [won]);

  useEffect(() => {
    if (!running || over || won) return;
    const speed = 2.2 + stack.length * 0.25;
    const step = () => {
      posRef.current += dirRef.current * speed;
      if (posRef.current >= TRACK - BRICK_W) {
        posRef.current = TRACK - BRICK_W;
        dirRef.current = -1;
      } else if (posRef.current <= 0) {
        posRef.current = 0;
        dirRef.current = 1;
      }
      setPos(posRef.current);
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [running, over, won, stack.length]);

  const start = () => {
    setStack([]);
    setOver(false);
    posRef.current = 0;
    dirRef.current = 1;
    setPos(0);
    setRunning(true);
  };

  const drop = () => {
    if (!running || over || won) return;
    const target = stack.length ? stack[stack.length - 1].left : (TRACK - BRICK_W) / 2;
    if (Math.abs(posRef.current - target) <= TOLERANCE) {
      setStack((s) => [...s, { left: posRef.current, color: BRICKS[s.length % BRICKS.length] }]);
    } else {
      setOver(true);
    }
  };

  const done = over || won;

  return (
    <div className="bg-white rounded-2xl border border-[#E6DCD3] p-6 sm:p-8 shadow-sm text-center" data-testid="stack-bricks-game">
      {won && burst && <LegoConfetti onDone={() => setBurst(false)} />}
      <div className="flex items-center justify-between mb-4">
        <div className="text-left">
          <h3 className="font-bold text-base text-[#1E252B]">Stack the Bricks</h3>
          <p className="text-xs text-[#736B63] mt-0.5">Tap to drop each sliding brick onto the tower. Stack {MAX_HEIGHT} to finish the build.</p>
        </div>
        <span className="text-xs font-bold text-[#D4691E]" data-testid="stack-score">
          {stack.length} / {MAX_HEIGHT} stacked
        </span>
      </div>

      <button
        type="button"
        onClick={done ? start : running ? drop : start}
        data-testid="stack-drop-btn"
        className="block w-full max-w-[300px] mx-auto rounded-xl border-2 border-dashed border-[#DCD3C9] bg-[#FBF9F5] p-2 cursor-pointer focus:outline-none focus:border-[#D4691E]"
        aria-label={running && !done ? "Drop brick" : "Start stacking"}
      >
        <div className="relative w-full h-[300px] overflow-hidden" data-testid="stack-board">
          {/* drop guide */}
          <div className="absolute bottom-0 left-0 right-0 h-2 bg-[#1E252B] rounded-sm" />
          {stack.map((b, i) => (
            <Brick key={i} color={b.color} style={{ left: `${b.left / TRACK * 100}%`, bottom: 8 + i * 26 }} />
          ))}
          {running && !done && (
            <Brick color={BRICKS[stack.length % BRICKS.length]} style={{ left: `${pos / TRACK * 100}%`, top: 8 }} moving />
          )}
          {!running && !done && stack.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-sm font-bold text-[#A39B92]">Tap to lay the first brick</span>
            </div>
          )}
          {done && (
            <div className="absolute inset-x-0 top-3 text-center">
              <span className="inline-block bg-[#141A1E] border border-[#3A4752] rounded-lg px-4 py-2 text-sm font-bold text-white" data-testid="stack-result">
                {won ? "Tower complete — master builder!" : `Toppled at ${stack.length} bricks!`}
              </span>
            </div>
          )}
        </div>
      </button>

      <div className="mt-4">
        {done && (
          <button
            type="button"
            onClick={start}
            data-testid="stack-retry-btn"
            className="px-6 py-2.5 rounded-lg bg-[#1E252B] hover:bg-[#D4691E] text-white text-sm font-bold transition-colors"
          >
            {won ? "Build Again" : "Rebuild the Tower"}
          </button>
        )}
        {done && stack.length > 0 && <ScoreSubmit game="stack-the-bricks" score={stack.length} scoreLabel="bricks" />}
      </div>
    </div>
  );
}
